import React from 'react';

import { useSelector } from 'react-redux';

import { useMutation, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import PropTypes from 'prop-types';

import { Text, TextGroup, Title } from '@asteria/component-core/typography';

import Form, { AutoSave } from '@asteria/component-form';
import { Switch } from '@asteria/component-form-v2';
import { useIntegrationName } from '@asteria/component-integrations-v2/hooks';
import Prefix from '@asteria/component-prefix';

import * as IntegrationStore from '@asteria/datalayer/stores/integrations';

import { Translation } from '@asteria/language';

import Slider from '../../../components/slider';

/**
 * @typedef { import('../../basic/types').Props } Props
 * @typedef { import('../../basic/types').ChildrenProps } ChildrenProps
 */

function usePrefix(integration) {
	const status = integration?.status?.state ?? 'IDLE';

	if (integration?.disabled) {
		return 'disabled';
	}

	if (integration?.config?.connected === false) {
		return 'not-connected';
	}

	if (status === 'IMPORTING') {
		return 'status-importing';
	}

	if (status === 'INITIATING') {
		return 'status-initiating';
	}

	if (status === 'ERROR') {
		return 'status-error';
	}

	return 'status-idle';
}

/** @type { React.FC<ChildrenProps & { id: string, accounts: unknown[] }> } */
const BankAccountsSlide = function BankAccountsSlide(props) {
	const { id, accounts, translationOptions } = props;

	const integration = useSelector(
		(store) => IntegrationStore.selectors.integration(store, id),
		isEqual,
	);

	const prefix = usePrefix(integration);

	const integrationName = useIntegrationName(
		integration?.type,
		integration?.key,
	);

	return (
		<div className="flex flex-col p-4 gap-4">
			<AutoSave key={id} debounce={1_000} enabled={!!accounts.length} />

			<div className="flex gap-2 justify-center items-center">
				<Prefix className={prefix} />
				<Title size="xxs" align="center">
					{integrationName}
				</Title>
			</div>
			<div className="border border-solid border-border-normal rounded">
				{!accounts.length ? (
					<div className="p-4 flex items-center justify-center">
						<Translation
							translationKey="card.content.accounts.empty"
							translationOptions={translationOptions}
							Component={Text}
							align="center"
						/>
					</div>
				) : (
					accounts.map((account) => (
						<div
							key={account?._id ?? account?.id}
							className="p-4 flex gap-2 justify-between items-center border-0 border-b border-solid border-border-normal last:border-0"
						>
							<div className="flex gap-2 items-center">
								<Switch
									name={[
										account?._id ?? account?.id,
										'active',
									].join('.')}
								/>
								<TextGroup>
									<Title size="xxs">{account?.name}</Title>
									{account?.identifiers?.number ? (
										<Text
											size="sm"
											className="text-content-text-muted"
										>
											{account?.identifiers?.number}
										</Text>
									) : null}
								</TextGroup>
							</div>
							<TextGroup>
								<Translation
									defaultText="{{ sums.display.total | number:false:false:true }} {{ sums.display.currency }}"
									data={account}
									Component={Text}
									size="sm"
									align="right"
									weight="medium"
								/>
								{account?.sums?.display?.currency !==
								account?.sums?.original?.currency ? (
									<Translation
										defaultText="{{ sums.original.total | number:false:false:true }} {{ sums.original.currency }}"
										data={account}
										Component={Text}
										size="xs"
										align="right"
									/>
								) : null}
							</TextGroup>
						</div>
					))
				)}
			</div>
		</div>
	);
};

BankAccountsSlide.propTypes = {
	id: PropTypes.string,
	accounts: PropTypes.arrayOf(PropTypes.object),
	translationOptions: PropTypes.object,
};

/** @type { React.FC<ChildrenProps> } */
const BankAccounts = function BankAccounts(props) {
	const { translationOptions, onSubmit } = props;

	const IDs = useSelector(
		(store) =>
			IntegrationStore.selectors
				.integrations(store, { type: 'bank' })
				.map((object) => object?._id ?? object?.id),
		isEqual,
	);

	const query = useQuery({
		queryKey: ['card', 'account', 'bank-accounts'],
		queryFn: async () =>
			onSubmit?.('card:fetch', { type: 'bank-accounts' }),

		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,

		keepPreviousData: true,
		enabled: !!IDs.length,

		placeholderData: { source: [], target: [] },

		select: (response) =>
			(response?.source ?? []).reduce((acc, object) => {
				const id = object?.meta?.source;

				return {
					...acc,
					[id]: [].concat(acc?.[id] ?? []).concat(object),
				};
			}, {}),
	});

	const values = React.useMemo(
		() =>
			Object.values(query.data)
				.flat()
				.reduce((acc, account) => {
					const id = account?._id ?? account?.id;

					return {
						...acc,
						[id]: {
							active: account?.active ?? true,
							identifiers: {
								number: account?.identifiers?.number,
							},
						},
					};
				}, {}),
		[query.data],
	);

	const mutation = useMutation({
		mutationFn: async (form) => {
			return onSubmit?.('card:submit', {
				type: 'bank-accounts',
				data: {
					form: Object.fromEntries(
						Object.entries(form).map(
							([key, { active, ...rest }]) => [
								key,
								{ ...rest, active: !!active },
							],
						),
					),
				},
			});
		},
	});

	if (!IDs.length || (query.isFetching && !query.isRefetching)) {
		return null;
	}

	return (
		<Form values={values} onSubmit={mutation.mutateAsync}>
			<Slider
				className="gap-4"
				items={IDs}
				translationOptions={translationOptions}
			>
				{({ action: id }) => (
					<BankAccountsSlide
						key={id}
						{...props}
						id={id}
						accounts={query.data?.[id] ?? []}
					/>
				)}
			</Slider>
		</Form>
	);
};

BankAccounts.propTypes = {
	translationOptions: PropTypes.object,
	onSubmit: PropTypes.func,
};

export default BankAccounts;
